/**
 * BotSpot Web Dashboard - Credentials Hook
 *
 * React hook for fetching and managing credential data.
 */

import { useState, useEffect, useCallback } from "react";
import { listCredentials } from "../api/credentials";
import { getErrorMessage } from "../api/client";
import type { Credential, CredentialList, PaginationParams } from "../api/types";

/**
 * Hook state interface
 */
interface UseCredentialsState {
  credentials: Credential[];
  total: number;
  loading: boolean;
  error: string | null;
}

/**
 * Hook return type
 */
interface UseCredentialsReturn extends UseCredentialsState {
  refetch: () => Promise<void>;
}

/**
 * Custom hook for fetching and managing credential data
 *
 * Features:
 * - Automatic fetching on mount
 * - Manual refetch capability
 * - Error handling
 *
 * @param params - Pagination parameters
 * @returns Credential data and refetch function
 */
export function useCredentials(
  params?: PaginationParams
): UseCredentialsReturn {
  const [state, setState] = useState<UseCredentialsState>({
    credentials: [],
    total: 0,
    loading: true,
    error: null,
  });

  /**
   * Fetch credentials from API
   */
  const fetchCredentials = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      const data: CredentialList = await listCredentials(params);

      setState({
        credentials: data.credentials,
        total: data.total,
        loading: false,
        error: null,
      });
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setState((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
    }
  }, [params]);

  /**
   * Initial fetch
   */
  useEffect(() => {
    fetchCredentials();
  }, [fetchCredentials]);

  return {
    ...state,
    refetch: fetchCredentials,
  };
}
